import { Component } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { withTranslation, WithTranslation } from "react-i18next";
import styled from "styled-components";
import englishTable from "./images/en_Wall.png";
import twTable from "./images/zh-TW_Wall.png";
import cnTable from "./images/zh-CN_Wall.png";
import demoPicture1 from "./images/demo1.png";
import demoPicture2 from "./images/demo2.png";
import demoPicture3 from "./images/demo3.png";

import { SetUpImage } from "../styles";
import {
  VideoListItem,
  OptionText,
  OrderList,
  Segment,
  SetUpSegment,
  Title,
  TitleIndicate,
} from "components/User/Evaluation/styles";
import { videoUploadTabs } from "constants/eval_constants";

const FormattedText = styled(OptionText)`
  white-space: pre-line;

  /* Style section headers (lines that are followed by numbered lists) */
  strong {
    font-weight: 600;
    display: block;
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: 17px;
  }

  /* First strong element doesn't need top margin */
  > strong:first-child {
    margin-top: 0;
  }
`;

interface SetUpProps {
  submitButton: (videoId: number) => JSX.Element;
  language: string;
}

interface SetUpState {}

type Props = SetUpProps & RouteComponentProps & WithTranslation;

class SetUp extends Component<Props, SetUpState> {
  getImage = () => {
    const { language } = this.props;
    switch (language) {
      case "en":
        return englishTable;
      case "zh-TW":
        return twTable;
      case "zh-CN":
        return cnTable;
      default:
        return "";
    }
  };

  formatText = (text: string) => {
    if (!text) return text;

    // Process the text to format section headers
    // Split by patterns like "Equipment\n1)", "Environment\n1)", "Timing\n1)", "Important!!!\n"
    const sections: JSX.Element[] = [];
    const lines = text.split("\n");
    let currentSection: string[] = [];
    let currentHeader: string | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Check if this is a section header (short line, no numbers, followed by numbered item)
      // Find the next non-empty line
      let nextNonEmptyLine = "";
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim()) {
          nextNonEmptyLine = lines[j].trim();
          break;
        }
      }

      const isHeader =
        trimmed.length > 0 &&
        trimmed.length < 50 &&
        !trimmed.match(/^\d+\)/) &&
        !trimmed.match(/^\d+\./) &&
        trimmed !== "Important!!!" &&
        nextNonEmptyLine.match(/^\d+\)/);

      // Check for "Important!!!" header
      const isImportantHeader = trimmed === "Important!!!";

      if (isHeader || isImportantHeader) {
        // Save previous section if exists
        if (currentSection.length > 0) {
          sections.push(
            <div key={sections.length} style={{ marginBottom: "16px" }}>
              {currentHeader && (
                <strong
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "17px",
                    fontWeight: 600,
                  }}
                >
                  {currentHeader}
                </strong>
              )}
              <div style={{ whiteSpace: "pre-line" }}>
                {currentSection.join("\n")}
              </div>
            </div>
          );
        }

        // Start new section
        currentHeader = trimmed;
        currentSection = [];
      } else if (trimmed === "Important!!!") {
        // This shouldn't happen since we check above, but just in case
        currentHeader = trimmed;
      } else {
        currentSection.push(line);
      }
    }

    // Add the last section
    if (currentSection.length > 0 || currentHeader) {
      const headerStyle =
        currentHeader === "Important!!!"
          ? {
              color: "#d32f2f",
              fontSize: "17px",
              fontWeight: 600,
              display: "block",
              marginBottom: "8px",
              marginTop: "12px",
            }
          : {
              display: "block",
              marginBottom: "8px",
              fontSize: "17px",
              fontWeight: 600,
            };

      sections.push(
        <div key={sections.length} style={{ marginBottom: "0" }}>
          {currentHeader && (
            <strong style={headerStyle}>{currentHeader}</strong>
          )}
          <div style={{ whiteSpace: "pre-line" }}>
            {currentSection.join("\n")}
          </div>
        </div>
      );
    }

    return sections.length > 0 ? sections : text;
  };

  render() {
    const { t } = this.props;
    const { submitButton } = this.props;
    return (
      <>
        <Segment>
          <TitleIndicate />
          <Title>{t("user.survey.corePro.video.setUp.alttitle")}</Title>
        </Segment>
        <SetUpSegment>
          {t("user.survey.corePro.video.setUp.guidelinesTitle") && (
            <Title style={{ fontSize: "24px", marginBottom: "20px" }}>
              {t("user.survey.corePro.video.setUp.guidelinesTitle")}
            </Title>
          )}

          <OrderList style={{ marginLeft: "20px", marginRight: "30px" }}>
            {t("user.survey.corePro.video.setUp.guidelines1") && (
              <VideoListItem>
                <FormattedText>
                  {this.formatText(
                    t("user.survey.corePro.video.setUp.guidelines1")
                  )}
                </FormattedText>
              </VideoListItem>
            )}

            {t("user.survey.corePro.video.setUp.guidelines3") && (
              <VideoListItem>
                <OptionText>
                  {t("user.survey.corePro.video.setUp.guidelines3")}
                </OptionText>
              </VideoListItem>
            )}

            {t("user.survey.corePro.video.setUp.guidelines4") && (
              <VideoListItem>
                <FormattedText>
                  {this.formatText(
                    t("user.survey.corePro.video.setUp.guidelines4")
                  )}
                </FormattedText>
              </VideoListItem>
            )}
          </OrderList>

          <SetUpImage
            className="center"
            src={this.getImage()}
            alt="Recording setup"
          />
          <SetUpImage
            className="center"
            src={demoPicture1}
            alt="Demo setup 1"
          />
          <SetUpImage
            className="center"
            src={demoPicture2}
            alt="Demo setup 2"
          />
          <SetUpImage
            className="center"
            src={demoPicture3}
            alt="Demo setup 3"
          />
        </SetUpSegment>
        {submitButton(videoUploadTabs.setup)}
      </>
    );
  }
}

export default withRouter(withTranslation()(SetUp));
